"use client"

import { useState } from "react"
import "./BuildingSystem.css"

const BuildingSystem = ({ gameState, setGameState, addNotification }) => {
  const [selectedCategory, setSelectedCategory] = useState("infrastructure")
  const [constructionQueue, setConstructionQueue] = useState([])
  const [selectedBuilding, setSelectedBuilding] = useState(null)

  const buildingCategories = {
    infrastructure: {
      name: "Infrastructure",
      icon: "🏗️",
      buildings: [
        {
          id: "habitat_dome",
          name: "Habitat Dome",
          icon: "🏠",
          description: "Houses colonists safely from harsh environment",
          cost: { materials: 100, energy: 50 },
          effects: { populationCapacity: 200, colonyHealth: 5 },
          buildTime: 30,
          requirements: {},
          unlocked: true,
        },
        {
          id: "solar_array",
          name: "Solar Array",
          icon: "☀️",
          description: "Generates clean energy from sunlight",
          cost: { materials: 75, knowledge: 25 },
          effects: { energyProduction: 25 },
          buildTime: 20,
          requirements: {},
          unlocked: true,
        },
        {
          id: "shield_generator",
          name: "Shield Generator",
          icon: "🛡️",
          description: "Protects colony from radiation and space debris",
          cost: { materials: 200, energy: 100, knowledge: 50 },
          effects: { radiationProtection: 80, debrisProtection: 90 },
          buildTime: 60,
          requirements: { engineering: 25 },
          unlocked: true,
        },
        {
          id: "communication_array",
          name: "Communication Array",
          icon: "📡",
          description: "Maintains contact with Earth and other colonies",
          cost: { materials: 120, energy: 80, knowledge: 40 },
          effects: { communicationRange: 100, knowledgeBonus: 5 },
          buildTime: 45,
          requirements: {},
          unlocked: true,
        },
      ],
    },
    production: {
      name: "Production",
      icon: "🏭",
      buildings: [
        {
          id: "hydroponic_farm",
          name: "Hydroponic Farm",
          icon: "🌱",
          description: "Produces food using advanced growing techniques",
          cost: { materials: 60, water: 30, energy: 20 },
          effects: { foodProduction: 30 },
          buildTime: 25,
          requirements: {},
          unlocked: true,
        },
        {
          id: "mining_facility",
          name: "Mining Facility",
          icon: "⛏️",
          description: "Extracts raw materials from the planet",
          cost: { materials: 80, energy: 40 },
          effects: { materialProduction: 25 },
          buildTime: 35,
          requirements: {},
          unlocked: true,
        },
        {
          id: "water_reclamation",
          name: "Water Reclamation Plant",
          icon: "💧",
          description: "Purifies and recycles water efficiently",
          cost: { materials: 50, energy: 25 },
          effects: { waterProduction: 20 },
          buildTime: 30,
          requirements: {},
          unlocked: true,
        },
        {
          id: "oxygen_generator",
          name: "Oxygen Generator",
          icon: "🫁",
          description: "Produces breathable oxygen for colonists",
          cost: { materials: 90, energy: 60, water: 20 },
          effects: { oxygenProduction: 35 },
          buildTime: 40,
          requirements: {},
          unlocked: true,
        },
      ],
    },
    research: {
      name: "Research",
      icon: "🔬",
      buildings: [
        {
          id: "research_lab",
          name: "Research Laboratory",
          icon: "🧪",
          description: "Conducts scientific research and experiments",
          cost: { materials: 90, energy: 30, knowledge: 20 },
          effects: { knowledgeProduction: 15 },
          buildTime: 40,
          requirements: {},
          unlocked: true,
        },
        {
          id: "genetics_lab",
          name: "Genetics Laboratory",
          icon: "🧬",
          description: "Studies and modifies genetic traits",
          cost: { materials: 150, energy: 60, knowledge: 100 },
          effects: { mutationRate: 1.5, evolutionPoints: 10 },
          buildTime: 80,
          requirements: { biology: 25 },
          unlocked: true,
        },
        {
          id: "advanced_lab",
          name: "Advanced Research Lab",
          icon: "🔬",
          description: "Cutting-edge research facility for breakthrough discoveries",
          cost: { materials: 200, energy: 100, knowledge: 150 },
          effects: { knowledgeProduction: 30, researchSpeed: 2 },
          buildTime: 100,
          requirements: { intelligence: 30 },
          unlocked: true,
        },
      ],
    },
    medical: {
      name: "Medical",
      icon: "🏥",
      buildings: [
        {
          id: "medical_bay",
          name: "Medical Bay",
          icon: "🏥",
          description: "Treats sick and injured colonists",
          cost: { materials: 80, energy: 40, knowledge: 30 },
          effects: { healthBonus: 15, diseaseResistance: 50 },
          buildTime: 50,
          requirements: {},
          unlocked: true,
        },
        {
          id: "quarantine_facility",
          name: "Quarantine Facility",
          icon: "🦠",
          description: "Isolates and treats infectious diseases",
          cost: { materials: 120, energy: 60, knowledge: 50 },
          effects: { diseaseResistance: 80, quarantineCapacity: 100 },
          buildTime: 60,
          requirements: {},
          unlocked: true,
        },
      ],
    },
    recreation: {
      name: "Recreation",
      icon: "🎮",
      buildings: [
        {
          id: "recreation_center",
          name: "Recreation Center",
          icon: "🎮",
          description: "Provides entertainment and boosts morale",
          cost: { materials: 70, energy: 30 },
          effects: { moraleBonus: 20, stressReduction: 15 },
          buildTime: 35,
          requirements: {},
          unlocked: true,
        },
        {
          id: "sports_complex",
          name: "Sports Complex",
          icon: "🏃",
          description: "Keeps colonists physically fit and happy",
          cost: { materials: 100, energy: 50 },
          effects: { moraleBonus: 25, healthBonus: 10 },
          buildTime: 45,
          requirements: {},
          unlocked: true,
        },
      ],
    },
  }

  const canBuild = (building) => {
    const hasResources = Object.entries(building.cost).every(
      ([resource, amount]) => gameState.resources[resource] >= amount,
    )

    const meetsRequirements = Object.entries(building.requirements || {}).every(([skill, amount]) => {
      return gameState.user[skill] >= amount
    })

    const inQueue = constructionQueue.some((item) => item.building.id === building.id)
    const alreadyBuilt = gameState.buildings.some((b) => b.id === building.id)

    return hasResources && meetsRequirements && !inQueue && !alreadyBuilt
  }

  const getCannotBuildReason = (building) => {
    const missingResources = Object.entries(building.cost).filter(
      ([resource, amount]) => gameState.resources[resource] < amount,
    )
    if (missingResources.length > 0) {
      return `Need: ${missingResources.map(([r, a]) => `${a} ${r}`).join(", ")}`
    }

    const missingSkills = Object.entries(building.requirements || {}).filter(([skill, amount]) => {
      return gameState.user[skill] < amount
    })
    if (missingSkills.length > 0) {
      return `Need: ${missingSkills.map(([s, a]) => `${s} ${a}`).join(", ")}`
    }

    const inQueue = constructionQueue.some((item) => item.building.id === building.id)
    if (inQueue) return "Already in construction queue"

    const alreadyBuilt = gameState.buildings.some((b) => b.id === building.id)
    if (alreadyBuilt) return "Already built"

    return ""
  }

  const startConstruction = (building) => {
    if (!canBuild(building)) {
      addNotification({
        type: "error",
        title: "Cannot Build",
        description: getCannotBuildReason(building),
      })
      return
    }

    // Deduct resources
    setGameState((prev) => ({
      ...prev,
      resources: {
        ...prev.resources,
        ...Object.fromEntries(
          Object.entries(building.cost).map(([resource, amount]) => [
            resource,
            Math.max(0, prev.resources[resource] - amount),
          ]),
        ),
      },
    }))

    const constructionItem = {
      id: Date.now(),
      building: building,
      timeRemaining: building.buildTime,
      totalTime: building.buildTime,
      startTime: Date.now(),
    }

    setConstructionQueue((prev) => [...prev, constructionItem])

    addNotification({
      type: "success",
      title: "Construction Started",
      description: `${building.name} construction has begun!`,
    })

    // Start construction timer
    const interval = setInterval(() => {
      setConstructionQueue((prev) => {
        const updated = prev.map((item) => {
          if (item.id === constructionItem.id) {
            const newTimeRemaining = item.timeRemaining - 1
            if (newTimeRemaining <= 0) {
              // Construction complete
              setGameState((prevState) => ({
                ...prevState,
                buildings: [
                  ...prevState.buildings,
                  {
                    id: building.id,
                    name: building.name,
                    icon: building.icon,
                    health: 100,
                    maxHealth: 100,
                    level: 1,
                    effects: building.effects,
                  },
                ],
                user: {
                  ...prevState.user,
                  engineering: prevState.user.engineering + 1,
                },
              }))

              addNotification({
                type: "success",
                title: "Construction Complete",
                description: `${building.name} is now operational!`,
              })

              clearInterval(interval)
              return null
            }
            return { ...item, timeRemaining: newTimeRemaining }
          }
          return item
        })
        return updated.filter(Boolean)
      })
    }, 1000)
  }

  const repairBuilding = (buildingId) => {
    const building = gameState.buildings.find((b) => b.id === buildingId)
    if (!building) return

    const repairCost = Math.floor((100 - building.health) * 2)
    if (gameState.resources.materials < repairCost) {
      addNotification({
        type: "error",
        title: "Insufficient Materials",
        description: `Need ${repairCost} materials to repair ${building.name}`,
      })
      return
    }

    setGameState((prev) => ({
      ...prev,
      resources: { ...prev.resources, materials: prev.resources.materials - repairCost },
      buildings: prev.buildings.map((b) => (b.id === buildingId ? { ...b, health: 100 } : b)),
    }))

    addNotification({
      type: "success",
      title: "Repair Complete",
      description: `${building.name} has been fully repaired!`,
    })
  }

  return (
    <div className="building-system">
      <div className="building-header">
        <h2 className="section-title">
          <span className="title-icon">🏗️</span>
          Construction & Buildings
        </h2>
        <div className="building-stats">
          <div className="stat-item">
            <span className="stat-icon">🏢</span>
            <span className="stat-value">{gameState.buildings.length}</span>
            <span className="stat-label">Built</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">🔨</span>
            <span className="stat-value">{constructionQueue.length}</span>
            <span className="stat-label">Building</span>
          </div>
        </div>
      </div>

      <div className="building-content">
        <div className="category-selector">
          {Object.entries(buildingCategories).map(([categoryId, category]) => (
            <button
              key={categoryId}
              className={`category-btn ${selectedCategory === categoryId ? "active" : ""}`}
              onClick={() => setSelectedCategory(categoryId)}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{category.name}</span>
            </button>
          ))}
        </div>

        <div className="buildings-grid">
          {buildingCategories[selectedCategory].buildings.map((building) => {
            const canBuildThis = canBuild(building)
            const reason = getCannotBuildReason(building)
            const inQueue = constructionQueue.find((item) => item.building.id === building.id)

            return (
              <div
                key={building.id}
                className={`building-card ${!canBuildThis ? "locked" : ""} ${selectedBuilding?.id === building.id ? "selected" : ""}`}
                onClick={() => setSelectedBuilding(building)}
              >
                <div className="building-icon-large">{building.icon}</div>
                <div className="building-info">
                  <h4 className="building-name">{building.name}</h4>
                  <p className="building-description">{building.description}</p>
                </div>

                <div className="building-cost">
                  <h5>Cost:</h5>
                  <div className="cost-list">
                    {Object.entries(building.cost).map(([resource, amount]) => (
                      <span
                        key={resource}
                        className={`cost-item ${gameState.resources[resource] < amount ? "insufficient" : ""}`}
                      >
                        {resource === "materials" && "🔩"}
                        {resource === "energy" && "⚡"}
                        {resource === "knowledge" && "🧠"}
                        {resource === "water" && "💧"}
                        {amount}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="building-effects">
                  <h5>Effects:</h5>
                  <div className="effects-list">
                    {Object.entries(building.effects).map(([effect, value]) => (
                      <span key={effect} className="effect-item">
                        {effect}: +{value}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="building-actions">
                  {inQueue ? (
                    <div className="building-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${((inQueue.totalTime - inQueue.timeRemaining) / inQueue.totalTime) * 100}%`,
                          }}
                        ></div>
                      </div>
                      <span className="progress-text">{inQueue.timeRemaining}s remaining</span>
                    </div>
                  ) : canBuildThis ? (
                    <button className="build-btn" onClick={() => startConstruction(building)}>
                      🔨 Build ({building.buildTime}s)
                    </button>
                  ) : (
                    <div className="cannot-build">
                      <span className="cannot-build-reason">{reason}</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {constructionQueue.length > 0 && (
          <div className="construction-queue">
            <h3 className="section-title">
              <span className="title-icon">🔨</span>
              Construction Queue
            </h3>
            <div className="queue-list">
              {constructionQueue.map((item) => (
                <div key={item.id} className="queue-item">
                  <div className="queue-info">
                    <span className="queue-icon">{item.building.icon}</span>
                    <span className="queue-name">{item.building.name}</span>
                  </div>
                  <div className="queue-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${((item.totalTime - item.timeRemaining) / item.totalTime) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">{item.timeRemaining}s</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {gameState.buildings.length > 0 && (
          <div className="built-buildings">
            <h3 className="section-title">
              <span className="title-icon">🏢</span>
              Operational Buildings
            </h3>
            <div className="built-list">
              {gameState.buildings.map((building) => (
                <div key={building.id} className="built-item">
                  <div className="built-info">
                    <span className="built-icon">{building.icon}</span>
                    <div className="built-details">
                      <span className="built-name">{building.name}</span>
                      <span className="built-level">Level {building.level}</span>
                    </div>
                  </div>
                  <div className="built-health">
                    <div className="health-bar">
                      <div
                        className="health-fill"
                        style={{
                          width: `${building.health}%`,
                          backgroundColor:
                            building.health > 70 ? "#4caf50" : building.health > 30 ? "#ff9800" : "#f44336",
                        }}
                      ></div>
                    </div>
                    <span className="health-text">{building.health}%</span>
                    {building.health < 100 && (
                      <button
                        className="repair-btn"
                        onClick={() => repairBuilding(building.id)}
                        title={`Repair cost: ${Math.floor((100 - building.health) * 2)} materials`}
                      >
                        🔧 Repair
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BuildingSystem
